import {
  deletePackFile,
  listPackFiles,
  openPackForWrite,
  readPackFile,
} from "./opfs";
import {
  forgetLocalPack,
  packFileName,
  packTotalBytes,
  saveLocalPacks,
  type PackDescriptor,
} from "./manifest";
import { fetchChart } from "../network/chartFetch";
import { ensurePackSearch, removePackSearch } from "./search";

/** How much is written before the transfer is committed to the real file. */
const CHECKPOINT_BYTES = 32 * 1024 * 1024;

export interface InstallProgress {
  pack: PackDescriptor;
  received: number;
  total: number;
}

export type InstallState = "missing" | "partial" | "installed";

/**
 * A pack's own file length is its install record: a complete file matches the
 * manifest byte count exactly, a short one is a resumable partial, and anything
 * else is missing. That keeps a second source of truth (and its chance of
 * disagreeing with the file system) out of the design.
 */
export async function installStates(
  packs: PackDescriptor[],
): Promise<Map<string, InstallState>> {
  const files = await listPackFiles();
  const states = new Map<string, InstallState>();
  for (const pack of packs) {
    const size = files.get(packFileName(pack));
    if (size === undefined) states.set(pack.id, "missing");
    else if (size >= pack.bytes) states.set(pack.id, "installed");
    else states.set(pack.id, "partial");
  }
  return states;
}

export async function bytesOnDisk(pack: PackDescriptor): Promise<number> {
  return (await readPackFile(packFileName(pack)))?.size ?? 0;
}

export async function removePack(pack: PackDescriptor): Promise<void> {
  await deletePackFile(packFileName(pack));
  await removePackSearch(pack);
  forgetLocalPack(pack.id);
}

/**
 * Streams a pack into OPFS, resuming from whatever is already on disk. Runs
 * only while the app is in the foreground - neither target platform offers
 * dependable background transfers.
 */
export async function installPack(
  pack: PackDescriptor,
  onProgress: (progress: InstallProgress) => void,
  signal?: AbortSignal,
): Promise<void> {
  const name = packFileName(pack);
  const total = packTotalBytes(pack);
  let offset = await bytesOnDisk(pack);
  if (offset >= pack.bytes) {
    await ensurePackSearch(pack, {
      signal,
      onProgress: (received) =>
        onProgress({ pack, received: pack.bytes + received, total }),
    });
    onProgress({ pack, received: total, total });
    return;
  }

  const headers = new Headers();
  if (offset > 0) headers.set("Range", `bytes=${offset}-`);
  const response = await fetchChart(pack.file, {
    headers,
    signal,
    cache: "no-store",
  });
  if (!response.ok) {
    throw new Error(`Download failed for ${pack.label} (${response.status}).`);
  }
  // A server that ignores Range replies 200 with the whole file, so the partial
  // bytes on disk have to be discarded rather than appended to.
  if (offset > 0 && response.status !== 206) offset = 0;
  if (!response.body) throw new Error(`No response body for ${pack.label}.`);

  let writable = await openPackForWrite(name, { keepExisting: offset > 0 });
  let received = offset;
  let sinceCheckpoint = 0;
  let open = true;

  /**
   * Writes to a FileSystemWritableFileStream land in a swap file and only reach
   * the real file on close(); abort() throws the whole transfer away. So the
   * stream is closed and reopened periodically to commit a prefix, and any
   * failure closes rather than aborts, keeping the bytes already received.
   * Reopening with keepExistingData copies the file, hence a large interval -
   * a sync access handle in a worker would avoid the copy entirely and is the
   * natural upgrade if pack sizes grow.
   */
  const commit = async () => {
    await writable.close();
    open = false;
    writable = await openPackForWrite(name, { keepExisting: true });
    open = true;
    await writable.seek(received);
    sinceCheckpoint = 0;
  };

  try {
    if (offset > 0) await writable.seek(offset);
    const reader = response.body.getReader();
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      await writable.write(value);
      received += value.byteLength;
      sinceCheckpoint += value.byteLength;
      onProgress({ pack, received, total });
      if (sinceCheckpoint >= CHECKPOINT_BYTES) await commit();
    }
    await writable.close();
    open = false;
  } catch (error) {
    if (open) {
      // Commit the prefix received so far; the next attempt resumes from it.
      await writable.close().catch(async (closeError) => {
        console.warn(
          `klint: could not commit partial download of ${pack.id}:`,
          closeError,
        );
        await writable.abort().catch(() => undefined);
      });
    }
    throw error;
  }

  const finalSize = await bytesOnDisk(pack);
  if (finalSize !== pack.bytes) {
    throw new Error(
      `${pack.label} downloaded ${finalSize} bytes but the catalogue expects ${pack.bytes}.`,
    );
  }
  saveLocalPacks([pack]);
  await ensurePackSearch(pack, {
    signal,
    onProgress: (searchReceived) =>
      onProgress({ pack, received: pack.bytes + searchReceived, total }),
  });
  onProgress({ pack, received: total, total });
}
